import React from 'react';
import { useApp } from '../context/AppContext';

/**
 * LandingPage — the first screen the customer sees.
 * Shows: restaurant logo, name, tagline, social links, call button, and "View Menu" CTA.
 */
export default function LandingPage({ restaurant, onViewMenu }) {
  const { t, loc, theme } = useApp();
  const r = restaurant;

  return (
    <div style={styles.container}>
      {/* Decorative background pattern */}
      <div style={styles.bgPattern} />

      {/* Logo */}
      <div style={styles.logoWrap}>
        <img
          src={r.logo}
          alt={loc(r.name)}
          style={styles.logo}
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
      </div>

      {/* Restaurant name and tagline */}
      <h1 style={styles.name}>{loc(r.name)}</h1>
      <p style={styles.tagline}>{loc(r.tagline)}</p>

      {/* Address */}
      {r.address && (
        <p style={styles.address}>
          <span style={styles.addressIcon}>📍</span>
          {loc(r.address)}
        </p>
      )}

      {/* Social links */}
      <div style={styles.socialRow}>
        {r.instagram && (
          <a
            href={r.instagram}
            target="_blank"
            rel="noopener noreferrer"
            style={styles.socialLink}
            aria-label="Instagram"
          >
            <svg style={styles.socialIcon} viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
            </svg>
          </a>
        )}
        {r.facebook && (
          <a
            href={r.facebook}
            target="_blank"
            rel="noopener noreferrer"
            style={styles.socialLink}
            aria-label="Facebook"
          >
            <svg style={styles.socialIcon} viewBox="0 0 24 24" fill="currentColor">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
          </a>
        )}
        {r.phone && (
          <a
            href={`tel:${r.phone}`}
            style={styles.socialLink}
            aria-label="Call"
          >
            <svg style={styles.socialIcon} viewBox="0 0 24 24" fill="currentColor">
              <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
            </svg>
          </a>
        )}
      </div>

      {/* CTA Buttons */}
      <div style={styles.ctaGroup}>
        <button
          style={styles.primaryBtn}
          onClick={onViewMenu}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 8px 30px var(--accent-glow)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 4px 20px var(--accent-glow)';
          }}
        >
          {t('viewMenu')} ✦
        </button>

        {r.phone && (
          <a href={`tel:${r.phone}`} style={styles.secondaryBtn}>
            📞 {t('callUs')}
          </a>
        )}
      </div>

      {/* Bottom flourish */}
      <div style={styles.flourish}>✦ ✦ ✦</div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 'calc(100vh - 52px)',
    padding: '2rem 1.5rem',
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
    animation: 'fadeIn 0.5s ease',
  },
  bgPattern: {
    position: 'absolute',
    inset: 0,
    opacity: 0.03,
    backgroundImage: `radial-gradient(circle at 25% 25%, var(--accent) 1px, transparent 1px),
                       radial-gradient(circle at 75% 75%, var(--accent) 1px, transparent 1px)`,
    backgroundSize: '40px 40px',
    pointerEvents: 'none',
  },
  logoWrap: {
    width: '140px',
    height: '140px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '1.5rem',
    border: '2px solid var(--accent)',
    padding: '8px',
    animation: 'scaleIn 0.5s ease',
  },
  logo: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    borderRadius: '50%',
  },
  name: {
    fontFamily: 'var(--font-display)',
    fontSize: '2.2rem',
    fontWeight: 700,
    marginBottom: '0.4rem',
    color: 'var(--text-primary)',
    letterSpacing: '-0.02em',
    animation: 'fadeInUp 0.5s ease 0.1s both',
  },
  tagline: {
    fontSize: '1rem',
    color: 'var(--accent)',
    fontWeight: 400,
    marginBottom: '1rem',
    fontStyle: 'italic',
    animation: 'fadeInUp 0.5s ease 0.2s both',
  },
  address: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.35rem',
    fontSize: '0.85rem',
    color: 'var(--text-secondary)',
    marginBottom: '1.5rem',
    animation: 'fadeInUp 0.5s ease 0.3s both',
  },
  addressIcon: {
    fontSize: '0.9rem',
  },
  socialRow: {
    display: 'flex',
    gap: '0.75rem',
    marginBottom: '2rem',
    animation: 'fadeInUp 0.5s ease 0.4s both',
  },
  socialLink: {
    width: '44px',
    height: '44px',
    borderRadius: 'var(--radius-full)',
    border: '1.5px solid var(--border)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--text-secondary)',
    transition: 'all var(--transition)',
    backgroundColor: 'var(--bg-secondary)',
  },
  socialIcon: {
    width: '18px',
    height: '18px',
  },
  ctaGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    width: '100%',
    maxWidth: '280px',
    animation: 'fadeInUp 0.5s ease 0.5s both',
  },
  primaryBtn: {
    padding: '1rem 2rem',
    backgroundColor: 'var(--accent)',
    color: 'var(--text-inverse)',
    fontFamily: 'var(--font-body)',
    fontWeight: 600,
    fontSize: '1.05rem',
    borderRadius: 'var(--radius-full)',
    transition: 'all var(--transition)',
    boxShadow: '0 4px 20px var(--accent-glow)',
    letterSpacing: '0.03em',
  },
  secondaryBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    padding: '0.85rem 2rem',
    border: '1.5px solid var(--border)',
    backgroundColor: 'transparent',
    color: 'var(--text-primary)',
    fontFamily: 'var(--font-body)',
    fontWeight: 500,
    fontSize: '0.95rem',
    borderRadius: 'var(--radius-full)',
    transition: 'all var(--transition)',
    textDecoration: 'none',
  },
  flourish: {
    marginTop: '2.5rem',
    color: 'var(--accent)',
    fontSize: '0.7rem',
    letterSpacing: '0.5em',
    opacity: 0.5,
    animation: 'fadeInUp 0.5s ease 0.6s both',
  },
};
